// PDFViewer.js

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { openDB } from 'idb';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RefPopup from './RefPopup';

const PDF_VIEWER_LABELS = {
  en: {
    buttons: {
      sidebarToggleButton: { title: 'Toggle Sidebar', label: 'Toggle Sidebar' },
      viewFindButton: { title: 'Find in Document', label: 'Find' },
      previous: { title: 'Previous Page', label: 'Previous' },
      next: { title: 'Next Page', label: 'Next' },
      zoomOutButton: { title: 'Zoom Out', label: 'Zoom Out' },
      zoomInButton: { title: 'Zoom In', label: 'Zoom In' },
      editorHighlightButton: { title: 'Highlight', label: 'Highlight' },
      editorFreeTextButton: { title: 'Text', label: 'Text' },
      editorInkButton: { title: 'Draw', label: 'Draw' },
      editorStampButton: { title: 'Add or edit images', label: 'Add or edit images' },
      printButton: { title: 'Print', label: 'Print' },
      downloadButton: { title: 'Save', label: 'Save' },
      secondaryToolbarToggleButton: { title: 'Tools', label: 'Tools' },
      findPreviousButton: { title: 'Find the previous occurrence of the phrase', label: 'Previous' },
      findNextButton: { title: 'Find the next occurrence of the phrase', label: 'Next' },
    },
    inputs: {
      pageNumber: { title: 'Page' },
      findInput: { title: 'Find', placeholder: 'Find in document…' },
      scaleSelect: { title: 'Zoom' },
    },
    options: {
      pageAutoOption: 'Automatic Zoom',
      pageActualOption: 'Actual Size',
      pageFitOption: 'Page Fit',
      pageWidthOption: 'Page Width',
    },
    checkboxes: {
      findHighlightAll: 'Highlight All',
      findMatchCase: 'Match Case',
      findMatchDiacritics: 'Match Diacritics',
      findEntireWord: 'Whole Words',
    },
    numPages: (count) => `of ${count}`,
  },
  zh: {
    buttons: {
      sidebarToggleButton: { title: '切换侧边栏', label: '切换侧边栏' },
      viewFindButton: { title: '在文档中查找', label: '查找' },
      previous: { title: '上一页', label: '上一页' },
      next: { title: '下一页', label: '下一页' },
      zoomOutButton: { title: '缩小', label: '缩小' },
      zoomInButton: { title: '放大', label: '放大' },
      editorHighlightButton: { title: '高亮', label: '高亮' },
      editorFreeTextButton: { title: '文字', label: '文字' },
      editorInkButton: { title: '绘制', label: '绘制' },
      editorStampButton: { title: '添加或编辑图片', label: '图片' },
      printButton: { title: '打印', label: '打印' },
      downloadButton: { title: '保存', label: '保存' },
      secondaryToolbarToggleButton: { title: '工具', label: '工具' },
      findPreviousButton: { title: '查找上一处匹配内容', label: '上一处' },
      findNextButton: { title: '查找下一处匹配内容', label: '下一处' },
    },
    inputs: {
      pageNumber: { title: '页码' },
      findInput: { title: '查找', placeholder: '在文档中查找…' },
      scaleSelect: { title: '缩放' },
    },
    options: {
      pageAutoOption: '自动缩放',
      pageActualOption: '实际大小',
      pageFitOption: '适合页面',
      pageWidthOption: '适合宽度',
    },
    checkboxes: {
      findHighlightAll: '全部高亮',
      findMatchCase: '区分大小写',
      findMatchDiacritics: '匹配变音符号',
      findEntireWord: '完整单词',
    },
    numPages: (count) => `共 ${count} 页`,
  },
};

const normalizeViewerLanguage = (language) => (
  language?.startsWith('zh') ? 'zh' : 'en'
);

const updateViewerButton = (doc, id, config) => {
  if (!config) {
    return;
  }

  const button = doc.getElementById(id);
  if (!button) {
    return;
  }

  button.title = config.title;
  button.setAttribute('aria-label', config.title);

  const label = button.querySelector('span');
  if (label) {
    label.textContent = config.label;
  }
};

const updateViewerInput = (doc, id, config) => {
  if (!config) {
    return;
  }

  const input = doc.getElementById(id);
  if (!input) {
    return;
  }

  if (config.title) {
    input.title = config.title;
  }

  if (config.placeholder) {
    input.placeholder = config.placeholder;
  }
};

const updateViewerText = (doc, id, value) => {
  const element = doc.getElementById(id);
  if (element && element.textContent !== value) {
    element.textContent = value;
  }
};

const updateViewerCheckboxLabel = (doc, inputId, value) => {
  const label = doc.querySelector(`label[for="${inputId}"]`);
  if (label) {
    label.textContent = value;
  }
};

const updateViewerPageCount = (doc, strings) => {
  const numPages = doc.getElementById('numPages');
  if (!numPages) {
    return;
  }

  const match = numPages.textContent.match(/(\d+)/);
  if (match) {
    const localizedText = strings.numPages(match[1]);
    if (numPages.textContent !== localizedText) {
      numPages.textContent = localizedText;
    }
  }
};

const applyViewerLocalization = (iframeWindow, language) => {
  if (!iframeWindow?.document) {
    return;
  }

  const doc = iframeWindow.document;
  const viewerLanguage = normalizeViewerLanguage(language);
  const strings = PDF_VIEWER_LABELS[viewerLanguage];

  Object.entries(strings.buttons).forEach(([id, config]) => {
    updateViewerButton(doc, id, config);
  });

  Object.entries(strings.inputs).forEach(([id, config]) => {
    updateViewerInput(doc, id, config);
  });

  Object.entries(strings.options).forEach(([id, value]) => {
    updateViewerText(doc, id, value);
  });

  Object.entries(strings.checkboxes).forEach(([id, value]) => {
    updateViewerCheckboxLabel(doc, id, value);
  });

  doc.documentElement.lang = viewerLanguage === 'zh' ? 'zh-CN' : 'en-US';
  updateViewerPageCount(doc, strings);

  if (iframeWindow.__labcatNumPagesObserver) {
    iframeWindow.__labcatNumPagesObserver.disconnect();
  }

  const numPages = doc.getElementById('numPages');
  if (numPages && iframeWindow.MutationObserver) {
    const observer = new iframeWindow.MutationObserver(() => {
      updateViewerPageCount(doc, strings);
    });
    observer.observe(numPages, { childList: true, characterData: true, subtree: true });
    iframeWindow.__labcatNumPagesObserver = observer;
  }
};

const applyViewerTheme = (iframeWindow, isDarkMode) => {
  if (!iframeWindow?.document) {
    return;
  }

  iframeWindow.document.documentElement.classList.toggle('dark', isDarkMode);
  iframeWindow.document.body?.classList.toggle('dark', isDarkMode);
  iframeWindow.postMessage({ type: 'SET_DARK_MODE', isDark: isDarkMode }, '*');
};

const upsertViewerTranslateAction = (
  iframeWindow,
  { visible, title, subtitle, ariaLabel, onClick }
) => {
  if (!iframeWindow?.document) {
    return;
  }

  const doc = iframeWindow.document;
  const toolbarRight = doc.getElementById('toolbarViewerRight');
  const toolsToggle = doc.getElementById('secondaryToolbarToggle');

  if (!toolbarRight) {
    return;
  }

  let separator = doc.getElementById('labcatTranslateSeparator');
  let button = doc.getElementById('labcatTranslateButton');

  if (!visible) {
    separator?.remove();
    button?.remove();
    return;
  }

  if (!separator) {
    separator = doc.createElement('div');
    separator.id = 'labcatTranslateSeparator';
    separator.className = 'verticalToolbarSeparator labcat-toolbar-action-separator';
  }

  if (!button) {
    button = doc.createElement('button');
    button.id = 'labcatTranslateButton';
    button.type = 'button';
    button.className = 'toolbarButton labcat-toolbar-action';

    const copy = doc.createElement('div');
    copy.className = 'labcat-toolbar-action-copy';

    const titleElement = doc.createElement('span');
    titleElement.className = 'labcat-toolbar-action-title';

    const subtitleElement = doc.createElement('span');
    subtitleElement.className = 'labcat-toolbar-action-subtitle';

    copy.append(titleElement, subtitleElement);
    button.append(copy);
  }

  button.title = ariaLabel;
  button.setAttribute('aria-label', ariaLabel);
  button.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClick();
  };

  const titleElement = button.querySelector('.labcat-toolbar-action-title');
  const subtitleElement = button.querySelector('.labcat-toolbar-action-subtitle');

  if (titleElement) {
    titleElement.textContent = title;
  }

  if (subtitleElement) {
    subtitleElement.textContent = subtitle;
  }

  toolbarRight.insertBefore(separator, toolsToggle || null);
  toolbarRight.insertBefore(button, toolsToggle || null);
};

const PDFViewer = ({ pdfId, pdfUrl, onTextSelection, onPDFLoad, isDarkMode }) => {
  const iframeRef = useRef(null);
  const blobUrlRef = useRef(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [citationInfo, setCitationInfo] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const showToolbarTranslateAction = !location.pathname.includes('/translate') && !pdfUrl;
  const translateActionTitle = t('pdf.translateDocumentTitle');
  const translateActionSubtitle = t('pdf.translateDocumentSubtitle');
  const translateActionAria = t('pdf.translateDocumentAria');

  const extractTextFromAllPages = async (pdfDocument) => {
    if (!pdfDocument) {
      console.error('PDF document is null or undefined');
      return '';
    }
    const numPages = pdfDocument.numPages;
    let fullText = '';
    for (let i = 1; i <= numPages; i++) {
      try {
        const page = await pdfDocument.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      } catch (error) {
        console.error(`Error extracting text from page ${i}:`, error);
      }
    }
    return fullText;
  };

  const saveAnnotationsToStorage = useCallback(async (pdfId, annotations) => {
    try {
      const db = await openDB('PDFStore', 4, {
        upgrade(db, oldVersion, newVersion, transaction) {
          if (!db.objectStoreNames.contains('annotations')) {
            db.createObjectStore('annotations');
          }
        },
      });

      // Directly save the annotations object
      await db.put('annotations', annotations, pdfId);
    } catch (error) {
      console.error('Error saving annotations:', error);
    }
  }, []);

  const getAnnotationsFromStorage = useCallback(async (pdfId) => {
    const db = await openDB('PDFStore', 4);
    const annotations = await db.get('annotations', pdfId);
    return annotations;
  }, []);

  const updateLastOpened = useCallback(async (pdfId) => {
    try {
      const db = await openDB('PDFStore', 4);
      const metadata = await db.get('metadata', pdfId);
      if (metadata) {
        const updatedMetadata = { ...metadata, lastOpened: Date.now() };
        await db.put('metadata', updatedMetadata, pdfId);
      }
    } catch (error) {
      console.error('Error updating lastOpened:', error);
    }
  }, []);

  const handleTranslateFullText = useCallback(() => {
    navigate(`/translate/${pdfId}`);
  }, [navigate, pdfId]);

  const waitForPDFLoad = async (iframeWindow, maxRetries = 5, retryDelay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      if (iframeWindow.PDFViewerApplication.pdfDocument) {
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
    return false;
  };

  useEffect(() => {
    const loadPDF = async () => {
      try {
        let blobUrl;
        let isWebUrl = false;
        let directWebUrl = null;

        // First check if we have a direct web PDF URL from the web PDF feature
        if (pdfUrl && typeof pdfUrl === 'object' && pdfUrl.isWebUrl) {
          isWebUrl = true;
          directWebUrl = pdfUrl.webUrl;
          console.log("Loading PDF directly from URL:", directWebUrl);
        } else if (pdfUrl) {
          // Use the provided pdfUrl directly (blob URL)
          blobUrl = pdfUrl;
        } else {
          // Load from IndexedDB using pdfId
          const db = await openDB('PDFStore', 4);
          const pdfBlob = await db.get('pdfs', pdfId);

          if (pdfBlob) {
            // Revoke previous blob URL to prevent memory leak
            if (blobUrlRef.current) {
              URL.revokeObjectURL(blobUrlRef.current);
            }
            blobUrl = URL.createObjectURL(pdfBlob);
            blobUrlRef.current = blobUrl;

            // Update lastOpened when PDF is loaded
            await updateLastOpened(pdfId);
          } else {
            console.log('PDF blob not found in database');
          }
        }

        if (blobUrl || directWebUrl) {
          // Build the URL for the PDF.js viewer
          let viewerUrlWithParams;
          
          if (isWebUrl && directWebUrl) {
            // For direct web URLs, pass the URL directly to the viewer
            viewerUrlWithParams = `${process.env.PUBLIC_URL}/pdfjs/web/viewer.html?file=${encodeURIComponent(directWebUrl)}`;
          } else if (blobUrl) {
            // For blob URLs (either from uploaded file or database)
            viewerUrlWithParams = `${process.env.PUBLIC_URL}/pdfjs/web/viewer.html?file=${encodeURIComponent(blobUrl)}`;
          }
          
          // Add pdfId if we have one (might not for direct web PDFs)
          if (pdfId) {
            viewerUrlWithParams += `&pdfId=${encodeURIComponent(pdfId)}`;
          }
          
          // Add zoom and page mode settings
          viewerUrlWithParams += '#zoom=auto&pagemode=none';
          
          setViewerUrl(viewerUrlWithParams);
        }
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadPDF();
  }, [pdfId, pdfUrl, updateLastOpened]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && viewerUrl) {
      iframe.onload = async () => {
        const iframeWindow = iframe.contentWindow;

        // Ensure PDF.js is initialized in the iframe (with 30s timeout)
        await new Promise((resolve, reject) => {
          const startTime = Date.now();
          const checkPDFJS = setInterval(() => {
            if (iframeWindow.PDFViewerApplication && iframeWindow.PDFViewerApplication.initialized) {
              clearInterval(checkPDFJS);
              resolve();
            } else if (Date.now() - startTime > 30000) {
              clearInterval(checkPDFJS);
              reject(new Error('PDF.js initialization timed out'));
            }
          }, 100);
        });

        // Wait for the PDF to be fully loaded with retry mechanism
        const isPDFLoaded = await waitForPDFLoad(iframeWindow);

        if (isPDFLoaded) {
          try {
            const pdfDocument = iframeWindow.PDFViewerApplication.pdfDocument;
            const fullText = await extractTextFromAllPages(pdfDocument);
            if (onPDFLoad) {
              onPDFLoad(fullText);
            }
          } catch (error) {
            console.error('Error extracting text from PDF:', error);
          }
        } else {
          console.error('PDF document failed to load after multiple attempts');
        }

        iframeWindow.postMessage({ type: 'ENABLE_TEXT_SELECTION_AND_ANNOTATIONS' }, '*');
        applyViewerLocalization(iframeWindow, i18n.language);
        upsertViewerTranslateAction(iframeWindow, {
          visible: showToolbarTranslateAction,
          title: translateActionTitle,
          subtitle: translateActionSubtitle,
          ariaLabel: translateActionAria,
          onClick: handleTranslateFullText,
        });
        applyViewerTheme(iframeWindow, isDarkMode);
      };
    }
  }, [
    viewerUrl,
    pdfId,
    onPDFLoad,
    getAnnotationsFromStorage,
    handleTranslateFullText,
    i18n.language,
    isDarkMode,
    showToolbarTranslateAction,
    translateActionAria,
    translateActionSubtitle,
    translateActionTitle,
  ]);

  useEffect(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (iframeWindow?.document?.readyState === 'complete') {
      applyViewerLocalization(iframeWindow, i18n.language);
      upsertViewerTranslateAction(iframeWindow, {
        visible: showToolbarTranslateAction,
        title: translateActionTitle,
        subtitle: translateActionSubtitle,
        ariaLabel: translateActionAria,
        onClick: handleTranslateFullText,
      });
      applyViewerTheme(iframeWindow, isDarkMode);
    }
  }, [
    handleTranslateFullText,
    i18n.language,
    isDarkMode,
    showToolbarTranslateAction,
    translateActionAria,
    translateActionSubtitle,
    translateActionTitle,
    viewerUrl,
  ]);

  useEffect(() => {
    const iframe = iframeRef.current;

    return () => {
      const iframeWindow = iframe?.contentWindow;
      iframeWindow?.__labcatNumPagesObserver?.disconnect();
      iframeWindow?.document?.getElementById('labcatTranslateSeparator')?.remove();
      iframeWindow?.document?.getElementById('labcatTranslateButton')?.remove();
      // Revoke blob URL on unmount to prevent memory leak
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const handleCitationHover = useCallback(async (event) => {
    let target = event.target;
  
    if (target.tagName !== 'A') {
      target = target.closest('a');
    }
  
    if (target && target.tagName === 'A' && target.getAttribute('data-element-id')) {
      const citationId = target.getAttribute('data-element-id');
      const href = target.getAttribute('href');

      if (!href) {
        return;
      }

      // Extract the destination from the href
      const destination = href.split('#')[1];
  
      // Safely decode the destination
      const safelyDecodedDestination = safeDecodeURIComponent(destination);
  
      // Get the reference content from PDF.js
      const iframeWindow = iframeRef.current.contentWindow;
      const PDFViewerApplication = iframeWindow.PDFViewerApplication;
      const pdfLinkService = PDFViewerApplication.pdfLinkService;
  
      try {
        const destInfo = await pdfLinkService.getDestinationInfo(safelyDecodedDestination);
  
        if (destInfo) {
          const { refNumber, refText } = destInfo;
  
          setCitationInfo(prevInfo => {
            if (prevInfo && prevInfo.id === citationId) {
              return prevInfo;
            }
            return {
              id: citationId,
              number: refNumber,
              text: refText,
              href: href
            };
          });
          setHoverPosition({ x: event.clientX, y: event.clientY });
          setIsPopupVisible(true);
        }
      } catch (error) {
        console.error('Error getting destination info:', error);
      }
    } else {
      setIsPopupVisible(false);
    }
  }, []);
  

const safeDecodeURIComponent = (str) => { 
  // Check if str is undefined or null
  if (str == null) {
    console.warn('Attempted to decode undefined or null string');
    return '';
  }

  // Ensure str is a string
  str = String(str);

  // Helper function to attempt decoding
  const attemptDecode = (s) => {
    try {
      return decodeURIComponent(s);
    } catch (e) {
      console.warn('Failed to decode:', s);
      return s;
    }
  };

  // Correctly handle special characters
  let decoded = str
    .replace(/%uFEFF/g, '\uFEFF')  // Zero width no-break space
    .replace(/%u2013/g, '–')       // En dash
    .replace(/%A0/g, '\u00A0');    // Non-breaking space
  
  // Attempt decoding multiple times to ensure all sequences are decoded
  let prevDecoded;
  do {
    prevDecoded = decoded;
    decoded = attemptDecode(decoded);
  } while (decoded !== prevDecoded && decoded.includes('%'));

  // If there are still encoded characters, decode them individually
  if (decoded.includes('%')) {
    decoded = decoded.replace(/%[0-9A-Fa-f]{2}/g, match => {
      return attemptDecode(match);
    });
  }

  return decoded;
};


  const handleMouseMove = useCallback((event) => {
    handleCitationHover(event);
  }, [handleCitationHover]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe) {
      const handleIframeLoad = () => {
        const iframeWindow = iframe.contentWindow;
        iframeWindow.addEventListener('mousemove', handleMouseMove);
      };

      iframe.addEventListener('load', handleIframeLoad);

      return () => {
        iframe.removeEventListener('load', handleIframeLoad);
        if (iframe.contentWindow) {
          iframe.contentWindow.removeEventListener('mousemove', handleMouseMove);
        }
      };
    }
  }, [handleMouseMove]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'textSelection') {
        onTextSelection(event.data.text);
      } else if (event.data && event.data.type === 'ANNOTATIONS_SAVED') {
        saveAnnotationsToStorage(pdfId, event.data.annotations);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onTextSelection, pdfId, saveAnnotationsToStorage]);


  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'citationHover') {
        const { pageNumber, refNumber, refText, x, y } = event.data.data;
        setCitationInfo({
          number: refNumber,
          text: refText,
          page: pageNumber
        });
        setHoverPosition({ x, y });
        setIsPopupVisible(true);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="relative h-full">
      <iframe
        ref={iframeRef}
        src={viewerUrl}
        width="100%"
        height="100%"
        title="PDF Viewer"
        className="absolute inset-0"
      />
      {isPopupVisible && (
        <RefPopup
          citationInfo={citationInfo}
          position={hoverPosition}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default PDFViewer;
